import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiErr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Loader2, CalendarDays, ClipboardCheck, Award, FileText, Printer, ClipboardList, Save, Lock, LockOpen } from "lucide-react";
import SatsExportDialog from "@/components/SatsExportDialog";
import SatsAttExportDialog from "@/components/SatsAttExportDialog";
import { toast } from "sonner";
import PrePrimarySats from "@/pages/PrePrimarySats";

const PRE_PRIMARY_CLASSES = ["Play Group", "LKG", "UKG"];

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function monthOptions() {
  // Last 12 months, newest first.
  const now = new Date();
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ value: val, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` });
  }
  return opts;
}

export default function TeacherUtility({ embedded } = {}) {
  const [sections, setSections] = useState([]);
  const [sel, setSel] = useState(""); // "class|section"
  const [loadingSections, setLoadingSections] = useState(true);
  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].value);

  const [att, setAtt] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [grades, setGrades] = useState(null);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [downloading, setDownloading] = useState("");

  // Summary report state
  const todayIso = new Date().toISOString().slice(0, 10);
  const [repExam, setRepExam] = useState("MT1");
  const [repDate, setRepDate] = useState(todayIso);
  const [report, setReport] = useState(null);
  const [repLoading, setRepLoading] = useState(false);
  const [repPdf, setRepPdf] = useState("");

  // SATS report state
  const [satsMode, setSatsMode] = useState("entry");
  const [satsExportOpen, setSatsExportOpen] = useState(false);
  const [satsAttOpen, setSatsAttOpen] = useState(false);
  const [satsExam, setSatsExam] = useState("FA1");
  const [satsMeta, setSatsMeta] = useState(null);
  const [satsSubject, setSatsSubject] = useState("");
  const [satsKind, setSatsKind] = useState("subject");
  const [satsRows, setSatsRows] = useState([]);
  const [satsInfo, setSatsInfo] = useState(null);
  const [satsDirty, setSatsDirty] = useState(() => new Set());
  const [satsLoading, setSatsLoading] = useState(false);
  const [satsSaving, setSatsSaving] = useState(false);
  const [satsLocking, setSatsLocking] = useState(false);
  const [satsStudent, setSatsStudent] = useState("");
  const [satsCard, setSatsCard] = useState(null);
  const [satsPdf, setSatsPdf] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState(null);

  // Guard SATS control switches so unsaved FA1/FA2 (or A1/A2) marks aren't lost.
  const guardSwitch = (fn) => {
    if (satsMode === "entry" && satsDirty.size > 0) setPendingSwitch(() => fn);
    else fn();
  };

  const [cls, section] = sel ? sel.split("|") : ["", ""];

  useEffect(() => {
    api.get("/teacher/utility/my-sections")
      .then(({ data }) => {
        const list = data.sections || [];
        setSections(list);
        if (list.length) setSel(`${list[0].class_name}|${list[0].section}`);
      })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load your classes"))
      .finally(() => setLoadingSections(false));
  }, []);

  const loadAttendance = useCallback(() => {
    if (!cls) return;
    setAttLoading(true);
    api.get(`/teacher/utility/attendance`, { params: { class_name: cls, section, month } })
      .then(({ data }) => setAtt(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load attendance"))
      .finally(() => setAttLoading(false));
  }, [cls, section, month]);

  const loadGrades = useCallback(() => {
    if (!cls) return;
    setGradesLoading(true);
    api.get(`/teacher/utility/grades`, { params: { class_name: cls, section } })
      .then(({ data }) => setGrades(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load grades"))
      .finally(() => setGradesLoading(false));
  }, [cls, section]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);
  useEffect(() => { loadGrades(); }, [loadGrades]);

  const download = async (kind) => {
    if (!cls) return;
    setDownloading(kind);
    try {
      const path = kind === "attendance"
        ? `/teacher/utility/attendance/export`
        : `/teacher/utility/grades/export`;
      const params = kind === "attendance" ? { class_name: cls, section, month } : { class_name: cls, section };
      const res = await api.get(path, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = kind === "attendance"
        ? `Attendance_Class${cls}-${section}_${month}.xlsx`
        : `Grades_Class${cls}-${section}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Download failed");
    } finally { setDownloading(""); }
  };

  const loadReport = useCallback(() => {
    if (!cls) return;
    setRepLoading(true);
    api.get(`/teacher/utility/summary-report`, { params: { class_name: cls, section, exam_key: repExam } })
      .then(({ data }) => setReport(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load report"))
      .finally(() => setRepLoading(false));
  }, [cls, section, repExam]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const reportPdf = async (mode) => {
    if (!cls) return;
    setRepPdf(mode);
    try {
      const res = await api.get(`/teacher/utility/summary-report/pdf`, {
        params: { class_name: cls, section, exam_key: repExam, date: repDate }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url; a.download = `Summary_${repExam}_Class${cls}-${section}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        const w = window.open(url, "_blank");
        if (w) w.onload = () => { try { w.print(); } catch (_e) {} };
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not generate PDF");
    } finally { setRepPdf(""); }
  };

  const repExams = (grades?.exams || []).length ? grades.exams : [
    { key: "MT1", short: "FA-1" }, { key: "MT2", short: "FA-2" }, { key: "SEM1", short: "I TERM" },
    { key: "MT3", short: "FA-3" }, { key: "MT4", short: "FA-4" }, { key: "SEM2", short: "II TERM" },
  ];

  // ---- SATS report ----
  useEffect(() => {
    if (!cls) return;
    api.get("/sats/report/meta", { params: { class_name: cls } })
      .then(({ data }) => {
        setSatsMeta(data);
        const first = (data.subjects[0] || data.grade_only_subjects[0] || "");
        setSatsSubject(first);
        setSatsKind(data.subjects.includes(first) ? "subject" : "grade");
      }).catch(() => setSatsMeta(null));
  }, [cls]);

  const loadSats = useCallback(() => {
    if (!cls || !satsSubject) { setSatsRows([]); return; }
    setSatsLoading(true);
    api.get("/sats/report/entry", { params: { class_name: cls, section: "ALL", exam_key: satsExam, subject: satsSubject, kind: satsKind } })
      .then(({ data }) => {
        let students = data.students || [];
        // Default A1/A2 (and term FA1/FA2 — same fields) to their max marks so
        // teachers only need to REDUCE where required. Only fills blank cells.
        if ((data.kind || satsKind) === "subject") {
          const div = data.div || 2;
          const noR = !!data.no_r;
          const a1max = data.a1_max ?? satsMeta?.a1_max ?? 15;
          const a2max = data.a2_max ?? satsMeta?.a2_max ?? 15;
          students = students.map((r) => {
            const a1 = r.a1 == null ? a1max : r.a1;
            const a2 = r.a2 == null ? a2max : r.a2;
            if (noR) {
              // Class 1 FA-1: no R — Written counts in full, T = A1+A2+W.
              return { ...r, a1, a2, r: null, t: (a1 || 0) + (a2 || 0) + (r.w || 0) };
            }
            const rr = r.w != null ? Math.floor(r.w / div + 0.5) : null;
            return { ...r, a1, a2, r: rr, t: (a1 || 0) + (a2 || 0) + (rr || 0) };
          });
        }
        setSatsRows(students); setSatsInfo(data); setSatsDirty(new Set());
      })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load SATS marks"))
      .finally(() => setSatsLoading(false));
  }, [cls, section, satsExam, satsSubject, satsKind, satsMeta]);
  useEffect(() => { loadSats(); }, [loadSats]);

  const cleanGrade = (g) => (g ? String(g).split("(")[0].trim() : g);
  const computeGoGrade = (mark, info) => {
    if (mark == null || mark === "" || isNaN(mark)) return null;
    const max = info?.grade_only_max || satsMeta?.grade_only_max || 100;
    const pct = (Number(mark) * 100) / max;
    const bands = [...(info?.grade_bands || [])].sort((a, b) => b.min - a.min);
    for (const b of bands) if (pct >= b.min) return cleanGrade(b.grade);
    return cleanGrade(info?.fail_grade || "");
  };

  const satsField = (sid, field, value) => {
    const div = satsInfo?.div || 2;
    const noR = !!satsInfo?.no_r;
    setSatsRows((rows) => rows.map((r) => {
      if (r.student_id !== sid) return r;
      if (field === "grade") {
        return { ...r, grade: value || null };
      }
      const v = value === "" ? null : Number(value);
      const nr = { ...r, [field]: v };
      if (satsKind === "subject") {
        if (noR) {
          nr.r = null;
          nr.t = (nr.a1 || 0) + (nr.a2 || 0) + (nr.w || 0);
        } else {
          const rr = nr.w != null ? Math.floor(nr.w / div + 0.5) : null;
          nr.r = rr;
          nr.t = (nr.a1 || 0) + (nr.a2 || 0) + (rr || 0);
        }
      }
      return nr;
    }));
    setSatsDirty((d) => new Set(d).add(sid));
  };

  const saveSats = async () => {
    // For subject marks, persist EVERY row (so the defaulted 15s stick even when
    // a teacher only reduced a few). Grade-only subjects save just edited cells
    // and store the numeric MARK (grade is auto-calculated from the %).
    const cells = satsKind === "subject"
      ? satsRows.map((r) => ({ student_id: r.student_id, a1: r.a1 ?? null, a2: r.a2 ?? null }))
      : satsRows.filter((r) => satsDirty.has(r.student_id)).map((r) => ({ student_id: r.student_id, grade: r.grade ?? null }));
    if (!cells.length) { toast.message("Nothing to save."); return; }
    setSatsSaving(true);
    try {
      await api.put("/sats/report/entry", { class_name: cls, section: "ALL", exam_key: satsExam, subject: satsSubject, kind: satsKind, cells });
      toast.success("SATS marks saved.");
      loadSats();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSatsSaving(false); }
  };

  // ---- SATS lock (class teacher only) ----
  const satsLocked = !!satsInfo?.locked;
  const satsCanLock = !!satsInfo?.can_lock;
  const satsLockedBy = satsInfo?.locked_by;
  // Locked blocks the class teacher; admins (can_lock=false) can still edit.
  const satsReadOnly = satsLocked && satsCanLock;
  const toggleSatsLock = async () => {
    setSatsLocking(true);
    try {
      await api.post(`/sats/report/${satsLocked ? "unlock" : "lock"}`, { class_name: cls, section, exam_key: satsExam });
      toast.success(satsLocked ? "SATS report unlocked — marks can be edited." : "SATS report locked. Marks are now read-only.");
      loadSats();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not update lock"); }
    finally { setSatsLocking(false); }
  };

  const loadSatsCard = (sid) => {
    if (!sid) { setSatsCard(null); return; }
    api.get("/sats/report/card", { params: { class_name: cls, section: "ALL", student_id: sid, exam_key: satsExam } })
      .then(({ data }) => setSatsCard(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load card"));
  };

  const downloadSatsPdf = async () => {
    if (!satsStudent) { toast.message("Pick a student first."); return; }
    setSatsPdf(true);
    try {
      const res = await api.get("/sats/report/card/pdf", { params: { class_name: cls, section: "ALL", student_id: satsStudent, exam_key: satsExam }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "PDF failed"); }
    finally { setSatsPdf(false); }
  };
  const satsAllSubjects = satsMeta ? [...satsMeta.subjects.map((s) => ({ name: s, kind: "subject" })), ...satsMeta.grade_only_subjects.map((s) => ({ name: s, kind: "grade" }))] : [];

  // One SATS block (subject table + grade-only chips). Used both stacked and side-by-side.
  const renderSatsBlock = (blk, compact = false) => (
    <div key={blk.key} data-testid={`sats-block-${blk.key}`}>
      <div className="text-base font-bold text-indigo-800 dark:text-indigo-300 mb-2" data-testid={`sats-block-label-${blk.key}`}>{blk.full_label || blk.label}</div>
      <div className="overflow-x-auto">
        <table className={`w-full ${compact ? "text-xs" : "text-sm"}`}>
          <thead className="bg-indigo-50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 text-left">
            <tr>
              <th className="px-2 py-2">Subject</th>
              <th className="px-2 py-2 text-center">{blk.col1}/{satsCard.a1_max}</th>
              <th className="px-2 py-2 text-center">{blk.col2}/{satsCard.a2_max}</th>
              <th className="px-2 py-2 text-center">W</th>
              {!blk.no_r && <th className="px-2 py-2 text-center">R (W÷{blk.div})</th>}
              <th className="px-2 py-2 text-center">T</th>
              <th className="px-2 py-2 text-center">G</th>
            </tr>
          </thead>
          <tbody>
            {blk.rows.map((r) => (
              <tr key={r.subject} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-2 py-1.5 font-medium text-slate-800 dark:text-slate-100">{r.subject}</td>
                <td className="px-2 py-1.5 text-center">{r.a1 ?? "-"}</td>
                <td className="px-2 py-1.5 text-center">{r.a2 ?? "-"}</td>
                <td className="px-2 py-1.5 text-center">{r.w ?? "-"}</td>
                {!blk.no_r && <td className="px-2 py-1.5 text-center">{r.r ?? "-"}</td>}
                <td className="px-2 py-1.5 text-center font-semibold text-indigo-700 dark:text-indigo-300">{r.t ?? "-"}</td>
                <td className="px-2 py-1.5 text-center font-semibold">{r.grade ?? "-"}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 font-bold">
              <td className="px-2 py-1.5">Total</td><td colSpan={2}></td>
              <td className="px-2 py-1.5 text-center" data-testid={`sats-total-w-${blk.key}`}>{blk.total_w}</td>
              {!blk.no_r && <td></td>}
              <td className="px-2 py-1.5 text-center" data-testid={`sats-total-t-${blk.key}`}>{blk.total_t}</td><td></td>
            </tr>
            <tr className="bg-slate-50 dark:bg-slate-900/40 font-semibold text-indigo-700 dark:text-indigo-300">
              <td className="px-2 py-1.5">Percentage</td><td colSpan={2}></td>
              <td className="px-2 py-1.5 text-center" data-testid={`sats-pct-w-${blk.key}`}>{blk.w_pct}% {blk.w_grade}</td>
              {!blk.no_r && <td></td>}
              <td className="px-2 py-1.5 text-center" data-testid={`sats-pct-t-${blk.key}`}>{blk.t_pct}% {blk.t_grade}</td><td></td>
            </tr>
          </tbody>
        </table>
      </div>
      {blk.grade_only.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
          <span className="font-semibold text-slate-500 uppercase tracking-wide">Grade-only:</span>
          {blk.grade_only.map((g) => (
            <span key={g.subject} data-testid={`sats-go-${blk.key}-${g.subject}`}>{g.subject}: <b className="text-emerald-700 dark:text-emerald-300">{g.grade || "-"}</b>{g !== blk.grade_only[blk.grade_only.length - 1] ? " ·" : ""}</span>
          ))}
        </div>
      )}
    </div>
  );

  // Group blocks: two consecutive FA blocks render side-by-side (register style); Term full width.
  const groupSatsBlocks = (blocks) => {
    const groups = [];
    let i = 0;
    while (i < blocks.length) {
      if (blocks[i].kind === "fa" && blocks[i + 1] && blocks[i + 1].kind === "fa") {
        groups.push([blocks[i], blocks[i + 1]]); i += 2;
      } else { groups.push([blocks[i]]); i += 1; }
    }
    return groups;
  };

  const Wrap = embedded ? Fragment : Layout;
  return (
    <Wrap>
      <div className="space-y-6" data-testid="teacher-utility-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Utility</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Monthly attendance and consolidated grades for your class.</p>
          </div>
          {sections.length > 1 && (
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="w-[200px]" data-testid="utility-section-select">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent>
                {sections.map((s) => (
                  <SelectItem key={`${s.class_name}|${s.section}`} value={`${s.class_name}|${s.section}`}>
                    Class {s.class_name} - {s.section}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {loadingSections ? (
          <div className="flex items-center gap-2 text-slate-500 py-16 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : sections.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="utility-no-section">
            You are not assigned as a class teacher of any section, so there's nothing to show here.
          </div>
        ) : (
          <>
            <div className="text-sm font-medium text-slate-600 dark:text-slate-300">
              Class {cls} - {section}
            </div>
            <Tabs defaultValue="attendance" className="w-full">
              <TabsList data-testid="utility-tabs">
                <TabsTrigger value="attendance" data-testid="utility-tab-attendance"><ClipboardCheck className="h-4 w-4 mr-1.5" /> Attendance</TabsTrigger>
                <TabsTrigger value="grades" data-testid="utility-tab-grades"><Award className="h-4 w-4 mr-1.5" /> Grades</TabsTrigger>
                <TabsTrigger value="report" data-testid="utility-tab-report"><FileText className="h-4 w-4 mr-1.5" /> Summary Report</TabsTrigger>
                <TabsTrigger value="sats" data-testid="utility-tab-sats"><ClipboardList className="h-4 w-4 mr-1.5" /> SATS Report</TabsTrigger>
              </TabsList>

              {/* Attendance */}
              <TabsContent value="attendance" className="mt-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-slate-400" />
                    <Select value={month} onValueChange={setMonth}>
                      <SelectTrigger className="w-[180px]" data-testid="utility-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {att && <span className="text-xs text-slate-400">{att.class_days_marked} day(s) marked this month</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setSatsAttOpen(true)} disabled={attLoading}
                      className="border-emerald-400 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                      data-testid="utility-attendance-sats">
                      <Download className="h-4 w-4 mr-2" /> Export SATS Attendance
                    </Button>
                    <Button onClick={() => download("attendance")} disabled={downloading === "attendance" || attLoading} data-testid="utility-attendance-download">
                      {downloading === "attendance" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download Excel
                    </Button>
                  </div>
                </div>
                <SatsAttExportDialog open={satsAttOpen} onOpenChange={setSatsAttOpen} classroom={cls} month={month} />
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-sm" data-testid="utility-attendance-table">
                    <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 text-left">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Roll No</th>
                        <th className="px-4 py-2.5 font-medium">Student ID</th>
                        <th className="px-4 py-2.5 font-medium">Name</th>
                        <th className="px-4 py-2.5 font-medium text-center">Present</th>
                        <th className="px-4 py-2.5 font-medium text-center">Absent</th>
                        <th className="px-4 py-2.5 font-medium text-center">Total Marked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attLoading ? (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                      ) : (att?.students || []).length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No students in this section.</td></tr>
                      ) : att.students.map((s) => (
                        <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`utility-att-row-${s.id}`}>
                          <td className="px-4 py-2.5 text-slate-500">{s.roll_number}</td>
                          <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{s.student_id}</td>
                          <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{s.name}</td>
                          <td className="px-4 py-2.5 text-center text-emerald-600 font-semibold">{s.present}</td>
                          <td className="px-4 py-2.5 text-center text-rose-500 font-semibold">{s.absent}</td>
                          <td className="px-4 py-2.5 text-center text-slate-600 dark:text-slate-300">{s.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Grades */}
              <TabsContent value="grades" className="mt-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-400">Totals across all subjects for each exam</span>
                  <Button onClick={() => download("grades")} disabled={downloading === "grades" || gradesLoading} data-testid="utility-grades-download">
                    {downloading === "grades" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download Excel
                  </Button>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                  <table className="w-full text-sm" data-testid="utility-grades-table">
                    <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 text-left">
                      <tr>
                        <th className="px-4 py-2.5 font-medium">Roll No</th>
                        <th className="px-4 py-2.5 font-medium">Student ID</th>
                        <th className="px-4 py-2.5 font-medium">Name</th>
                        {(grades?.exams || []).map((e) => (
                          <th key={e.key} className="px-4 py-2.5 font-medium text-center whitespace-nowrap">{e.short}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {gradesLoading ? (
                        <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                      ) : (grades?.students || []).length === 0 ? (
                        <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No students in this section.</td></tr>
                      ) : grades.students.map((s) => (
                        <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`utility-grade-row-${s.id}`}>
                          <td className="px-4 py-2.5 text-slate-500">{s.roll_number}</td>
                          <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{s.student_id}</td>
                          <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap">{s.name}</td>
                          {(grades.exams || []).map((e) => (
                            <td key={e.key} className="px-4 py-2.5 text-center whitespace-nowrap text-slate-600 dark:text-slate-300">{s[e.short]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Summary Report */}
              <TabsContent value="report" className="mt-4 space-y-4">
                {(() => {
                  const dObj = repDate ? new Date(repDate + "T00:00:00") : new Date();
                  const dayName = dObj.toLocaleDateString("en-US", { weekday: "long" });
                  const monthYear = dObj.toLocaleDateString("en-US", { month: "long", year: "numeric" });
                  const dateDisp = dObj.toLocaleDateString("en-GB");
                  return (
                    <>
                      <div className="flex flex-wrap items-end justify-between gap-3">
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Examination</label>
                            <Select value={repExam} onValueChange={setRepExam}>
                              <SelectTrigger className="w-[180px]" data-testid="report-exam-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {repExams.map((e) => <SelectItem key={e.key} value={e.key}>{e.short}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Exam Date</label>
                            <input type="date" value={repDate} onChange={(e) => setRepDate(e.target.value)}
                              className="h-10 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm" data-testid="report-date-input" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" onClick={() => reportPdf("print")} disabled={!!repPdf || repLoading} data-testid="report-print-btn">
                            {repPdf === "print" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />} Print
                          </Button>
                          <Button onClick={() => reportPdf("download")} disabled={!!repPdf || repLoading} data-testid="report-pdf-btn">
                            {repPdf === "download" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download PDF
                          </Button>
                        </div>
                      </div>

                      {repLoading ? (
                        <div className="py-16 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                      ) : !report ? (
                        <div className="py-16 text-center text-slate-400">Select an examination to view its report.</div>
                      ) : (
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-6" data-testid="report-card">
                          {/* Header */}
                          <div className="relative text-center border-b border-slate-100 dark:border-slate-800 pb-4">
                            <img src="/trishul-logo.png" alt="logo" className="absolute left-0 top-0 h-14 w-14 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                            <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-300" style={{ fontFamily: "Outfit" }}>{report.school_name}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">K.B. Extension, Davangere</div>
                            <div className="mt-2 text-base font-semibold text-slate-800 dark:text-slate-100">{report.heading} · {monthYear}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Class {report.class_name} - {report.section} · Date: {dateDisp} · Day: {dayName}</div>
                          </div>

                          {/* Subject table */}
                          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                            <table className="w-full text-sm" data-testid="report-subject-table">
                              <thead className="bg-indigo-50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 text-left">
                                <tr>
                                  <th className="px-4 py-2.5 font-semibold">Subjects</th>
                                  <th className="px-4 py-2.5 font-semibold text-center">Pass</th>
                                  <th className="px-4 py-2.5 font-semibold text-center">Fail</th>
                                  <th className="px-4 py-2.5 font-semibold text-center">Absent</th>
                                  <th className="px-4 py-2.5 font-semibold text-center">Total</th>
                                  <th className="px-4 py-2.5 font-semibold text-center">Percentage</th>
                                </tr>
                              </thead>
                              <tbody>
                                {report.subjects.length === 0 ? (
                                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No marks recorded for this exam yet.</td></tr>
                                ) : report.subjects.map((r) => (
                                  <tr key={r.name} className="border-t border-slate-100 dark:border-slate-800" data-testid={`report-subj-${r.name}`}>
                                    <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{r.name}</td>
                                    <td className="px-4 py-2.5 text-center text-emerald-600 font-semibold">{r.pass}</td>
                                    <td className="px-4 py-2.5 text-center text-rose-500 font-semibold">{r.fail}</td>
                                    <td className="px-4 py-2.5 text-center text-amber-600 font-semibold">{r.absent}</td>
                                    <td className="px-4 py-2.5 text-center text-slate-600 dark:text-slate-300">{r.total}</td>
                                    <td className="px-4 py-2.5 text-center font-semibold text-indigo-700 dark:text-indigo-300">{r.percentage}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="text-sm text-slate-700 dark:text-slate-200" data-testid="report-class-pct">
                            <span className="font-semibold">Class percentage:</span> {report.class_percentage.passed_all}/{report.class_percentage.total} × 100 = <span className="font-bold text-indigo-700 dark:text-indigo-300">{report.class_percentage.pct}%</span>
                            <span className="text-xs text-slate-400 ml-1">(students who passed all subjects)</span>
                          </div>

                          {/* Grade list */}
                          <div>
                            <div className="text-sm font-bold text-rose-700 dark:text-rose-300 mb-2">Grade List</div>
                            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 inline-block min-w-[320px]">
                              <table className="text-sm text-center" data-testid="report-grade-table">
                                <thead className="bg-indigo-50 dark:bg-indigo-950/40 text-rose-700 dark:text-rose-300">
                                  <tr>{report.grade_list.grades.map((g) => <th key={g.grade} className="px-5 py-2 font-bold border-r border-slate-200 dark:border-slate-800">{g.grade}</th>)}<th className="px-5 py-2 font-bold">Total</th></tr>
                                </thead>
                                <tbody>
                                  <tr className="font-bold text-slate-800 dark:text-slate-100">{report.grade_list.grades.map((g) => <td key={g.grade} className="px-5 py-2 border-t border-r border-slate-200 dark:border-slate-800">{g.count}</td>)}<td className="px-5 py-2 border-t border-slate-200 dark:border-slate-800">{report.grade_list.total}</td></tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Fail / absent list */}
                          <div>
                            <div className="text-sm font-bold text-rose-700 dark:text-rose-300 mb-2">{report.exam_short} — Fail / Absent Students List</div>
                            {report.fail_students.rows.length === 0 ? (
                              <div className="text-sm text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-4 py-3">No fails or absentees for this exam.</div>
                            ) : (
                              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                <table className="w-full text-sm" data-testid="report-fail-table">
                                  <thead className="bg-indigo-50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-200 text-left">
                                    <tr>
                                      <th className="px-3 py-2 font-semibold">#</th>
                                      <th className="px-3 py-2 font-semibold">Student Name</th>
                                      {report.fail_students.subjects.map((s) => <th key={s} className="px-3 py-2 font-semibold text-center">{s}</th>)}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {report.fail_students.rows.map((r, i) => (
                                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                                        <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                                        <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap">{r.name}</td>
                                        {report.fail_students.subjects.map((s) => {
                                          const v = r.cells[s];
                                          return <td key={s} className={`px-3 py-2 text-center font-semibold ${v === "F" ? "text-rose-500" : v === "Ab" ? "text-amber-600" : "text-slate-300"}`}>{v}</td>;
                                        })}
                                      </tr>
                                    ))}
                                    <tr className="border-t border-slate-200 dark:border-slate-700 bg-rose-50 dark:bg-rose-950/30 font-bold">
                                      <td className="px-3 py-2" colSpan={2}>Total (F + Ab)</td>
                                      {report.fail_students.subjects.map((s) => {
                                        const t = report.fail_students.totals.find((x) => x.subject === s);
                                        return <td key={s} className="px-3 py-2 text-center text-rose-700 dark:text-rose-300">{t ? t.count : 0}</td>;
                                      })}
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>

                          {/* Toppers */}
                          <div>
                            <div className="text-sm font-bold text-emerald-700 dark:text-emerald-300 mb-2">{report.exam_short} — Toppers List</div>
                            {report.toppers.length === 0 ? (
                              <div className="text-sm text-slate-400">No marks entered yet.</div>
                            ) : (
                              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                                <table className="w-full text-sm" data-testid="report-toppers-table">
                                  <thead className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200 text-left">
                                    <tr>
                                      <th className="px-4 py-2 font-semibold text-center">Rank</th>
                                      <th className="px-4 py-2 font-semibold">Student Name</th>
                                      <th className="px-4 py-2 font-semibold text-center">Marks</th>
                                      <th className="px-4 py-2 font-semibold text-center">%</th>
                                      <th className="px-4 py-2 font-semibold text-center">Grade</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {report.toppers.map((r, i) => (
                                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                                        <td className="px-4 py-2 text-center font-bold text-emerald-700 dark:text-emerald-300">{r.rank}</td>
                                        <td className="px-4 py-2 font-medium text-slate-800 dark:text-slate-100 whitespace-nowrap">{r.name}</td>
                                        <td className="px-4 py-2 text-center text-slate-600 dark:text-slate-300">{r.marks}/{r.max}</td>
                                        <td className="px-4 py-2 text-center text-slate-600 dark:text-slate-300">{r.pct != null ? `${r.pct}%` : "-"}</td>
                                        <td className="px-4 py-2 text-center font-semibold text-indigo-700 dark:text-indigo-300">{r.grade}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </TabsContent>

              {/* SATS Report */}
              <TabsContent value="sats" className="mt-4 space-y-4">
                {PRE_PRIMARY_CLASSES.includes(cls) ? (
                  <PrePrimarySats cls={cls} section={section} />
                ) : (
                <>
                <AlertDialog open={!!pendingSwitch} onOpenChange={(o) => { if (!o) setPendingSwitch(null); }}>
                  <AlertDialogContent data-testid="sats-unsaved-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Unsaved marks</AlertDialogTitle>
                      <AlertDialogDescription>
                        You have {satsDirty.size} unsaved mark{satsDirty.size === 1 ? "" : "s"} in this subject. Switching now will discard {satsDirty.size === 1 ? "it" : "them"}. Save first, or continue to discard?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="sats-guard-cancel">Keep editing</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="sats-guard-discard"
                        onClick={() => { const fn = pendingSwitch; setSatsDirty(new Set()); setPendingSwitch(null); if (fn) fn(); }}
                      >Discard &amp; switch</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5">
                    <button onClick={() => setSatsMode("entry")} className={`px-3 py-1.5 text-sm rounded-md ${satsMode === "entry" ? "bg-indigo-600 text-white" : "text-slate-600 dark:text-slate-300"}`} data-testid="sats-mode-entry">Enter marks</button>
                    <button onClick={() => guardSwitch(() => setSatsMode("report"))} className={`px-3 py-1.5 text-sm rounded-md ${satsMode === "report" ? "bg-indigo-600 text-white" : "text-slate-600 dark:text-slate-300"}`} data-testid="sats-mode-report">Report cards</button>
                  </div>
                  <div className="ml-auto">
                    <Select value={satsExam} onValueChange={(v) => guardSwitch(() => setSatsExam(v))}>
                      <SelectTrigger className="w-[150px]" data-testid="sats-exam-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(satsMeta?.exams || [{ key: "FA1", label: "FA-1" }, { key: "FA2", label: "FA-2" }, { key: "SA1", label: "1st Term" }, { key: "FA3", label: "FA-3" }, { key: "FA4", label: "FA-4" }, { key: "SA2", label: "2nd Term" }]).map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {satsMode === "entry" ? (
                  <>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Subject</label>
                        <Select value={satsSubject ? `${satsKind}:${satsSubject}` : ""} onValueChange={(v) => guardSwitch(() => { const [k, ...rest] = v.split(":"); setSatsKind(k); setSatsSubject(rest.join(":")); })}>
                          <SelectTrigger className="w-[220px]" data-testid="sats-subject-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>
                            {satsMeta?.subjects?.length > 0 && <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">Scholastic</div>}
                            {satsMeta?.subjects?.map((s) => <SelectItem key={`subject:${s}`} value={`subject:${s}`}>{s}</SelectItem>)}
                            {satsMeta?.grade_only_subjects?.length > 0 && <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 mt-1">Grade-only</div>}
                            {satsMeta?.grade_only_subjects?.map((s) => <SelectItem key={`grade:${s}`} value={`grade:${s}`}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={saveSats}
                        disabled={
                          satsSaving || satsReadOnly || satsRows.length === 0 ||
                          // For grade-only subjects we only persist edited cells,
                          // so a dirty flag is still meaningful. For subject
                          // marks we persist every row (including defaulted 15s),
                          // so the button must be clickable even if the teacher
                          // accepts every default without editing.
                          (satsKind !== "subject" && satsDirty.size === 0)
                        }
                        className="h-10 bg-indigo-600 hover:bg-indigo-700"
                        data-testid="sats-save-btn"
                      >
                        {satsSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save{satsDirty.size ? ` (${satsDirty.size})` : ""}
                      </Button>
                      <Button onClick={() => setSatsExportOpen(true)} variant="outline" className="h-10 border-emerald-400 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" data-testid="sats-export-btn">
                        <Download className="h-4 w-4 mr-2" /> Export SATS Excel
                      </Button>
                      <SatsExportDialog open={satsExportOpen} onOpenChange={setSatsExportOpen} classroom={cls} section={section} examKey={satsExam} />
                      {satsCanLock && (
                        <Button
                          onClick={toggleSatsLock}
                          disabled={satsLocking}
                          variant={satsLocked ? "outline" : "default"}
                          className={`h-10 ${satsLocked ? "border-emerald-400 text-emerald-700 dark:text-emerald-300" : "bg-rose-600 hover:bg-rose-700 text-white"}`}
                          title={satsLocked ? "Unlock so the marks can be edited again" : "Lock this Class + Exam so the marks can't be changed"}
                          data-testid="sats-lock-btn"
                        >
                          {satsLocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : satsLocked ? <LockOpen className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                          {satsLocked ? "Unlock" : "Lock"}
                        </Button>
                      )}
                      {satsLocked && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 px-3 py-1.5 text-xs font-semibold" data-testid="sats-locked-badge">
                          <Lock className="h-3.5 w-3.5" /> Locked{satsLockedBy ? ` by ${satsLockedBy}` : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {satsKind === "subject"
                        ? (satsInfo?.no_r
                            ? <>Enter <b>{satsInfo?.col1_label || "A1"}</b> (/{satsMeta?.a1_max}) and <b>{satsInfo?.col2_label || "A2"}</b> (/{satsMeta?.a2_max}). Written (W) is pulled automatically {satsInfo?.exam_label ? <>from the <b>{satsInfo.exam_label}</b> exam</> : "from the subject marks"}; <b>no R</b> for this test — T = {satsInfo?.col1_label || "A1"}+{satsInfo?.col2_label || "A2"}+W, grade from T%.</>
                            : <>Enter <b>{satsInfo?.col1_label || "A1"}</b> (/{satsMeta?.a1_max}) and <b>{satsInfo?.col2_label || "A2"}</b> (/{satsMeta?.a2_max}). Written (W) is pulled automatically {satsInfo?.exam_label ? <>from the <b>{satsInfo.exam_label}</b> exam</> : "from the subject marks"}; R = {satsInfo?.r_num && satsInfo?.r_den && Number(satsInfo.r_num) !== 1 ? <>(W × {satsInfo.r_num}) ÷ {satsInfo.r_den}</> : <>W÷{satsInfo?.div || 2}</>}, T = {satsInfo?.col1_label || "A1"}+{satsInfo?.col2_label || "A2"}+R, grade from T%.</>)
                        : <>Select the <b>grade</b> for this subject from the list (per your grade logic). No marks are entered.</>}
                    </div>
                    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto" data-testid="sats-entry-grid">
                      {satsLoading ? <div className="p-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                        : satsRows.length === 0 ? <div className="p-10 text-center text-slate-400">No students / no subject selected.</div>
                        : (
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 dark:bg-slate-900/40 text-left text-[11px] uppercase tracking-wide text-slate-500">
                              <tr>
                                <th className="px-4 py-2.5">Student</th>
                                {satsKind === "subject" ? (
                                  <>
                                    <th className="px-3 py-2.5 text-center">{satsInfo?.col1_label || "A1"}</th>
                                    <th className="px-3 py-2.5 text-center">{satsInfo?.col2_label || "A2"}</th>
                                    <th className="px-3 py-2.5 text-center">W</th>
                                    {!satsInfo?.no_r && <th className="px-3 py-2.5 text-center">R</th>}
                                    <th className="px-3 py-2.5 text-center">T</th>
                                    <th className="px-3 py-2.5 text-center">G</th>
                                  </>
                                ) : (<><th className="px-3 py-2.5 text-center">Grade</th></>)}
                              </tr>
                            </thead>
                            <tbody>
                              {satsRows.map((r) => (
                                <tr key={r.student_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`sats-row-${r.roll_number}`}>
                                  <td className="px-4 py-2">
                                    <div className="font-medium text-slate-900 dark:text-slate-100">{r.name}</div>
                                    <div className="text-[10px] text-slate-400 uppercase">{r.roll_number}{r.section ? ` · Sec ${r.section}` : ""}</div>
                                  </td>
                                  {satsKind === "subject" ? (
                                    <>
                                      <td className="px-2 py-2 text-center"><Input value={r.a1 ?? ""} onChange={(e) => satsField(r.student_id, "a1", e.target.value)} disabled={satsReadOnly || satsInfo?.a1_a2_derived} inputMode="decimal" className={`h-9 w-16 mx-auto text-center ${satsInfo?.a1_a2_derived ? "bg-slate-50 dark:bg-slate-800/60 text-slate-500" : ""}`} title={satsInfo?.a1_a2_derived ? `Auto: (${satsInfo?.col1_label} total ÷ 50) × ${satsMeta?.a1_max || 15}` : undefined} data-testid={`sats-a1-${r.student_id}`} /></td>
                                      <td className="px-2 py-2 text-center"><Input value={r.a2 ?? ""} onChange={(e) => satsField(r.student_id, "a2", e.target.value)} disabled={satsReadOnly || satsInfo?.a1_a2_derived} inputMode="decimal" className={`h-9 w-16 mx-auto text-center ${satsInfo?.a1_a2_derived ? "bg-slate-50 dark:bg-slate-800/60 text-slate-500" : ""}`} title={satsInfo?.a1_a2_derived ? `Auto: (${satsInfo?.col2_label} total ÷ 50) × ${satsMeta?.a2_max || 15}` : undefined} data-testid={`sats-a2-${r.student_id}`} /></td>
                                      <td className="px-3 py-2 text-center text-slate-500" data-testid={`sats-w-${r.student_id}`}>{r.w ?? "-"}</td>
                                      {!satsInfo?.no_r && <td className="px-3 py-2 text-center text-slate-500">{r.r ?? "-"}</td>}
                                      <td className="px-3 py-2 text-center font-semibold text-indigo-700 dark:text-indigo-300" data-testid={`sats-t-${r.student_id}`}>{r.t ?? "-"}</td>
                                      <td className="px-3 py-2 text-center font-semibold">{r.grade ?? "-"}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-2 py-2 text-center">
                                        <select
                                          value={r.grade ?? ""}
                                          onChange={(e) => satsField(r.student_id, "grade", e.target.value)}
                                          disabled={satsReadOnly}
                                          className="h-9 w-28 mx-auto text-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60"
                                          data-testid={`sats-grade-${r.student_id}`}
                                        >
                                          <option value="">—</option>
                                          {(satsInfo?.grade_options || []).map((g) => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Student</label>
                        <Select value={satsStudent} onValueChange={(v) => { setSatsStudent(v); loadSatsCard(v); }}>
                          <SelectTrigger className="w-[240px]" data-testid="sats-student-select"><SelectValue placeholder="Select a student…" /></SelectTrigger>
                          <SelectContent>
                            {satsRows.map((r) => <SelectItem key={r.student_id} value={r.student_id}>{r.roll_number} · {r.name}{r.section ? ` (${r.section})` : ""}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={downloadSatsPdf} disabled={satsPdf || !satsStudent} className="h-10 bg-indigo-600 hover:bg-indigo-700" data-testid="sats-pdf-btn">
                        {satsPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download PDF
                      </Button>
                    </div>
                    {!satsCard ? (
                      <div className="py-14 text-center text-slate-400">Pick a student to view their SATS report card.</div>
                    ) : (
                      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="sats-card">
                        <div className="text-center border-b border-slate-100 dark:border-slate-800 pb-3 mb-4">
                          <div className="text-xl font-bold text-indigo-900 dark:text-indigo-300" style={{ fontFamily: "Outfit" }}>{satsCard.school_name}</div>
                          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 mt-1">SATS Report Card</div>
                          <div className="text-sm text-slate-600 dark:text-slate-300 mt-1.5 font-medium" data-testid="sats-card-student">
                            <span className="font-semibold">Name:</span> {satsCard.student_name}
                            <span className="mx-2 text-slate-300">·</span>
                            <span className="font-semibold">Class &amp; Section:</span> {satsCard.class_name}-{satsCard.section}
                            <span className="mx-2 text-slate-300">·</span>
                            <span className="font-semibold">Roll No:</span> {satsCard.roll_number}
                          </div>
                        </div>
                        {groupSatsBlocks(satsCard.blocks || []).map((grp, gi) => (
                          grp.length === 2 ? (
                            <div key={gi} className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
                              {grp.map((blk) => renderSatsBlock(blk, true))}
                            </div>
                          ) : (
                            <div key={gi} className="mb-6">{renderSatsBlock(grp[0])}</div>
                          )
                        ))}
                      </div>
                    )}
                  </>
                )}
                </>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Wrap>
  );
}
